import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Progress } from "antd5";
import { Flex, Text } from "styles/utility-components";

import { Select } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import { UpdateCompanyRequest_EmployeeCountEnum_0 } from "lib/generated/app-service-gql/graphql";
import { fieldInteractionEvent } from "./tracking";

type ExperienceSelectionProps = {
  onSubmit: (value: {
    experience: string;
    employeeCount: UpdateCompanyRequest_EmployeeCountEnum_0;
  }) => void;
};

function toEmployeeCountEnum(employeeCount: string) {
  const employeeCountEnumMap: Record<string, UpdateCompanyRequest_EmployeeCountEnum_0> = {
    ONE_TO_49: UpdateCompanyRequest_EmployeeCountEnum_0.OneTo_49,
    FIFTY_TO_99: UpdateCompanyRequest_EmployeeCountEnum_0.FiftyTo_99,
    HUNDRED_TO_499: UpdateCompanyRequest_EmployeeCountEnum_0.HundredTo_499,
    FIVE_HUNDRED_PLUS: UpdateCompanyRequest_EmployeeCountEnum_0.FiveHundredPlus,
  };
  return employeeCountEnumMap[employeeCount];
}

function ExperienceSelection({ onSubmit }: ExperienceSelectionProps) {
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<{ experience: string; companySize: string }>();

  return (
    <form
      onSubmit={handleSubmit((value) =>
        onSubmit({
          experience: value.experience,
          employeeCount: toEmployeeCountEnum(value.companySize),
        }),
      )}
    >
      <Flex column gap={40}>
        <ProgressBar>
          <Progress percent={25} showInfo={false} />
        </ProgressBar>
        <Flex column gap={16}>
          <Text h1>Which best describes you?</Text>
          <Text>We personalise your app experience based on who you are.</Text>
        </Flex>
        <Select
          placeholder="Select experience level"
          options={[
            { label: "New to public sector", value: "none" },
            { label: "1-5 contracts won", value: "limited" },
            { label: "6-49 contracts won", value: "familiar" },
            { label: "50+ contracts won", value: "mature" },
            { label: "I work for a public sector organisation", value: "buyer" },
          ]}
          name={"experience"}
          label={"How many public sector contracts has your company won?"}
          control={control}
          rules={{
            required: true,
          }}
          onChange={(value) => {
            fieldInteractionEvent(value, "experience");
          }}
        />
        <Select
          placeholder="Select number of employees"
          options={[
            { label: "1-49", value: "ONE_TO_49" },
            { label: "50-99", value: "FIFTY_TO_99" },
            { label: "100-499", value: "HUNDRED_TO_499" },
            { label: "500+", value: "FIVE_HUNDRED_PLUS" },
          ]}
          name={"companySize"}
          label={"What is your company size?"}
          control={control}
          rules={{
            required: true,
          }}
        />
        <SubmitButton disabled={!isValid} type="submit">
          Continue
        </SubmitButton>
      </Flex>
    </form>
  );
}

const ProgressBar = styled.div({
  maxWidth: "200px",
  marginBottom: "56px",
});

const SubmitButton = styled(Button)({
  maxWidth: "90px",
});

export default ExperienceSelection;
